<template>
  <div class="merch-page">
    <div v-if="cancelled" class="cancel-banner">
      Köpet avbröts. Inga pengar har dragits.
    </div>

    <div class="merch-grid">
      <!-- Bildgalleri -->
      <div class="gallery">
        <div class="hero-image-wrap">
          <img
            :src="activeImage"
            :alt="product.name"
            class="hero-image"
          />
        </div>
        <div class="thumbs">
          <button
            v-for="(img, i) in images"
            :key="i"
            class="thumb"
            :class="{ 'thumb--active': activeIndex === i }"
            @click="activeIndex = i"
            :aria-label="`Visa bild ${i + 1}`"
          >
            <img :src="img" :alt="`${product.name} bild ${i + 1}`" />
          </button>
        </div>
      </div>

      <!-- Produktinfo -->
      <div class="info">
        <p class="eyebrow">Officiell merch</p>
        <h1 class="title">{{ product.name }}</h1>

        <p class="price">
          <span v-if="!loadingInfo">{{ product.priceSEK }} kr</span>
          <span v-else class="skeleton skeleton-price"></span>
        </p>

        <p class="description">
          Klassisk unisex t-shirt i mjuk bomull. Gör sig ungefär lika bra en svettig kväll på Skeppet, som hemma i tv-soffan.
        </p>

        <!-- Storleksval -->
        <div class="size-section">
          <div class="size-label-row">
            <label class="size-label">Storlek</label>
            <a
              href="#size-guide"
              class="size-guide-link"
              @click.prevent="showSizeGuide = !showSizeGuide"
            >
              Storleksguide
            </a>
          </div>
          <div class="sizes">
            <button
              v-for="s in product.sizes"
              :key="s"
              :class="['size-btn', { 'size-btn--active': selectedSize === s }]"
              @click="selectedSize = s"
            >
              {{ s }}
            </button>
          </div>
          <transition name="fade">
            <div v-if="showSizeGuide" class="size-guide">
              <div class="size-guide-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Storlek</th>
                      <th>Längd (cm)</th>
                      <th>Bredd (cm)</th>
                      <th>Halv bröstmått (cm)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>S</td><td>71,1</td><td>91,4</td><td>45,7</td></tr>
                    <tr><td>M</td><td>73,7</td><td>101,6</td><td>50,8</td></tr>
                    <tr><td>L</td><td>76,2</td><td>111,8</td><td>55,9</td></tr>
                    <tr><td>XL</td><td>78,7</td><td>122</td><td>61</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </transition>
        </div>

        <!-- Köp-knapp -->
        <button
          class="buy-btn"
          :disabled="!selectedSize || checkingOut || !product.available"
          @click="startCheckout"
        >
          <span v-if="checkingOut">Skickar dig till checkout…</span>
          <span v-else-if="!product.available">Inte tillgänglig just nu</span>
          <span v-else-if="!selectedSize">Välj storlek</span>
          <span v-else>Köp för {{ product.priceSEK }} kr</span>
        </button>

        <!-- Trygghetssignaler -->
        <ul class="trust-list">
          <li>Säker betalning via Stripe</li>
          <li>Tryckt och skickat från Sverige</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { useQuasar } from "quasar";

export default {
  name: "MerchPage",
  setup() {
    const $q = useQuasar();
    return { $q };
  },
  data() {
    return {
      // Produktdata laddas från backend (priset hämtas från env)
      product: {
        name: "LiveGBG T-shirt",
        priceSEK: 349,
        sizes: ["S", "M", "L", "XL"],
        available: true,
      },
      loadingInfo: true,
      // Bilder ligger i public/merch/
      images: [
        "/merch/tshirt-front.jpeg",
        "/merch/tshirt-hanger.jpeg",
        "/merch/tshirt-lifestyle-1.png",
        "/merch/tshirt-lifestyle-2.jpg",
      ],
      activeIndex: 0,
      selectedSize: "",
      showSizeGuide: false,
      checkingOut: false,
    };
  },
  computed: {
    activeImage() {
      return this.images[this.activeIndex];
    },
    cancelled() {
      return this.$route.query.avbruten === "1";
    },
  },
  methods: {
    async fetchInfo() {
      this.loadingInfo = true;
      try {
        const { data } = await axios.get(
          process.env.VUE_APP_API_URL + "merch/info"
        );
        this.product = { ...this.product, ...data };
      } catch (err) {
        // Faller tillbaka på defaults om backend inte svarar
        console.warn("Kunde inte hämta merch-info:", err.message || err);
      } finally {
        this.loadingInfo = false;
      }
    },
    async startCheckout() {
      if (!this.selectedSize) return;
      this.checkingOut = true;
      try {
        const { data } = await axios.post(
          process.env.VUE_APP_API_URL + "merch/checkout",
          { size: this.selectedSize }
        );
        if (data.url) {
          window.location.href = data.url;
        } else {
          throw new Error("Ingen checkout-URL returnerades");
        }
      } catch (err) {
        this.checkingOut = false;
        this.$q.notify({
          type: "negative",
          message:
            err.response?.data?.message ||
            "Kunde inte starta checkout. Försök igen.",
          position: "top",
        });
      }
    },
  },
  async created() {
    await this.fetchInfo();
  },
};
</script>

<style scoped>
.merch-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem 5rem;
}

.cancel-banner {
  background: var(--color-border-light, rgba(0, 0, 0, 0.05));
  border: 1px solid var(--color-border);
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin-bottom: 1.5rem;
  text-align: center;
}

.merch-grid {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 4rem;
  align-items: start;
}

/* Tillåt grid-barn att krympa under sitt content (annars trycker
   t.ex. storleksguidens tabell ut hela kolumnen) */
.merch-grid > * {
  min-width: 0;
}

/* ── Gallery ── */
.gallery {
  position: sticky;
  top: 110px;
}

.hero-image-wrap {
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  background: var(--color-bg-soft, rgba(0, 0, 0, 0.03));
  border-radius: 8px;
  margin-bottom: 1rem;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.thumbs {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
}

.thumb {
  aspect-ratio: 1;
  border: 2px solid transparent;
  border-radius: 6px;
  background: var(--color-bg-soft, rgba(0, 0, 0, 0.03));
  cursor: pointer;
  padding: 0;
  overflow: hidden;
  transition: border-color 0.15s ease, transform 0.15s ease;
}

.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.thumb:hover {
  border-color: var(--color-text-secondary);
}

.thumb--active {
  border-color: var(--color-accent);
}

/* ── Info ── */
.info {
  display: flex;
  flex-direction: column;
}

.eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--color-accent);
  margin: 0 0 0.5rem;
}

.title {
  font-family: "Playfair Display", serif;
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 900;
  line-height: 1.05;
  text-transform: uppercase;
  letter-spacing: -0.01em;
  margin: 0 0 1rem;
}

.price {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0 0 1.5rem;
  color: var(--color-text);
}

.skeleton-price {
  display: inline-block;
  width: 6rem;
  height: 1.4rem;
  background: var(--color-border-light, rgba(0, 0, 0, 0.08));
  border-radius: 4px;
}

.description {
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--color-text-secondary);
  margin: 0 0 2rem;
}

/* Sizes */
.size-section {
  margin-bottom: 2rem;
}

.size-label-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.6rem;
}

.size-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

.size-guide-link {
  font-size: 0.75rem;
  color: var(--color-text-secondary);
  text-decoration: underline;
  cursor: pointer;
}

.size-guide-link:hover {
  color: var(--color-accent);
}

.sizes {
  display: flex;
  gap: 0.5rem;
}

.size-btn {
  flex: 1;
  padding: 0.85rem;
  border: 1px solid var(--color-border);
  background: var(--color-bg);
  color: var(--color-text);
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  cursor: pointer;
  border-radius: 6px;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.size-btn:hover {
  border-color: var(--color-text);
}

.size-btn--active {
  background: var(--color-text);
  color: var(--color-bg);
  border-color: var(--color-text);
}

.size-guide {
  margin-top: 1rem;
  padding: 1rem;
  background: var(--color-bg-soft, rgba(0, 0, 0, 0.03));
  border-radius: 6px;
  font-size: 0.85rem;
}

.size-guide-table-wrap {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  margin-bottom: 0.6rem;
  -webkit-overflow-scrolling: touch;
}

.size-guide table {
  width: 100%;
  border-collapse: collapse;
  min-width: 300px;
}

.size-guide th,
.size-guide td {
  padding: 0.4rem 0.5rem;
  text-align: left;
  border-bottom: 1px solid var(--color-border);
}

.size-guide th {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-secondary);
  font-weight: 600;
}

.size-guide-note {
  font-size: 0.75rem;
  color: var(--color-text-secondary);
  margin: 0.5rem 0 0;
  font-style: italic;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Buy button */
.buy-btn {
  width: 100%;
  padding: 1rem 1.5rem;
  background: var(--color-accent);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition: opacity 0.15s ease, background 0.15s ease;
  margin-bottom: 1.5rem;
}

.buy-btn:hover:not(:disabled) {
  background: #aa0e00;
}

.buy-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Trust list */
.trust-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.8rem;
  color: var(--color-text-secondary);
}

.trust-list li {
  padding: 0.4rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 1px solid var(--color-border);
}

.trust-list li::before {
  content: "✓";
  color: var(--color-accent);
  font-weight: 700;
}

.trust-list li:last-child {
  border-bottom: none;
}

/* Responsive */
@media (max-width: 900px) {
  .merch-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .gallery {
    position: static;
  }
}

@media (max-width: 700px) {
  .merch-page {
    padding: 2rem 1rem 4rem;
  }

  .thumbs {
    grid-template-columns: repeat(4, 1fr);
    gap: 0.4rem;
  }
}
</style>
